/**
 * Single source of truth for chart layouts.
 * To add a new layout: add an entry to LAYOUT_CONFIGS with value, label, icon, and rowCounts.
 * rowCounts = [2, 3] means 2 charts in row 1, 3 in row 2.
 */
import React from 'react';
import { Square, Grid, LayoutThreeColumns, ViewStacked, LayoutSplit } from 'react-bootstrap-icons';

/** 2 panes on top, 3 on the bottom. Matches rowCounts [2, 3]. */
export function FiveChartLayoutIcon({ size = 16, color = 'currentColor', ...rest }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 32 32" fill={color} aria-hidden="true" {...rest}>
      <rect x="2" y="2" width="13" height="13" rx="2" />
      <rect x="17" y="2" width="13" height="13" rx="2" />
      <rect x="2" y="17" width="8" height="13" rx="2" />
      <rect x="12" y="17" width="8" height="13" rx="2" />
      <rect x="22" y="17" width="8" height="13" rx="2" />
    </svg>
  );
}

/** 3 panes on top, 3 on the bottom. Matches rowCounts [3, 3]. */
export function SixChartLayoutIcon({ size = 16, color = 'currentColor', ...rest }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 32 32" fill={color} aria-hidden="true" {...rest}>
      <rect x="2" y="2" width="8" height="13" rx="2" />
      <rect x="12" y="2" width="8" height="13" rx="2" />
      <rect x="22" y="2" width="8" height="13" rx="2" />
      <rect x="2" y="17" width="8" height="13" rx="2" />
      <rect x="12" y="17" width="8" height="13" rx="2" />
      <rect x="22" y="17" width="8" height="13" rx="2" />
    </svg>
  );
}

export const LAYOUT_CONFIGS = [
  { value: 'single', label: 'Single Chart', icon: <Square size={16} />, rowCounts: [1] },
  { value: 'side-by-side', label: 'Side by Side', icon: <LayoutSplit size={16} />, rowCounts: [2] },
  { value: 'stacked', label: 'Top/Bottom', icon: <ViewStacked size={16} />, rowCounts: [1, 1] },
  { value: '3-column', label: '3 Columns', icon: <LayoutThreeColumns size={16} />, rowCounts: [3] },
  { value: 'quadrant', label: 'Quadrant', icon: <Grid size={16} />, rowCounts: [2, 2] },
  { value: '5-chart', label: '5 Charts', icon: <FiveChartLayoutIcon size={16} />, rowCounts: [2, 3] },
  { value: '6-chart', label: '6 Charts', icon: <SixChartLayoutIcon size={16} />, rowCounts: [3, 3] },
];

/** Chart count for a layout (sum of rowCounts) */
export const getRequiredChartCount = (layout) => {
  const config = LAYOUT_CONFIGS.find((c) => c.value === layout);
  if (!config) return 1;
  return config.rowCounts.reduce((sum, n) => sum + n, 0);
};

/** Visible chart count: 1 on mobile, otherwise layout's required count */
export const getVisibleChartCount = (chartLayout, isMobile) => {
  if (isMobile) return 1;
  return getRequiredChartCount(chartLayout);
};
